import { Button } from '@gravity-ui/uikit';
import { useLazyDownloadPerGenderRegionReportQuery } from 'store/api';
import { useGenderPerRegionDataQuery } from 'store/api/private/charts-api/chart-api-data';
import { useEffect, useState } from 'react';
import 'chart.js/auto';
import { Bar } from 'react-chartjs-2';
import styles from './styles.module.scss';

interface ChartData {
	labels: string[];
	datasets: {
		label: string;
		data: number[];
		backgroundColor: string;
		borderColor: string;
		borderWidth: number;
	}[];
}

export const GenderPerRegionChart = () => {
	const [trigger, { isFetching }] = useLazyDownloadPerGenderRegionReportQuery();

	const { data: genderPerRegionData, isLoading, error } = useGenderPerRegionDataQuery(null, {
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
    });

	const [chartData, setChartData] = useState<ChartData>({
		labels: [],
		datasets: [
			{
				label: 'Женщины',
				data: [],
				backgroundColor: 'rgba(240, 166, 167, 0.7)',
				borderColor: '#f0a6a7',
				borderWidth: 1,
			},
			{
				label: 'Мужчины',
				data: [],
				backgroundColor: 'rgba(124, 167, 174, 0.7)',
				borderColor: '#7ca7ae',
				borderWidth: 1,
			},
		],
	});

	useEffect(() => {
		if (genderPerRegionData) {
			const labels = genderPerRegionData.map((item) => item.region);
			const femaleData = genderPerRegionData.map((item) => item.female);
			const maleData = genderPerRegionData.map((item) => item.male);

			setChartData({
				labels: labels,
				datasets: [
					{
						label: 'Женщины',
						data: femaleData,
						backgroundColor: 'rgba(240, 166, 167, 0.7)',
						borderColor: '#f0a6a7',
						borderWidth: 1,
					},
					{
						label: 'Мужчины',
						data: maleData,
						backgroundColor: 'rgba(124, 167, 174, 0.7)',
						borderColor: '#7ca7ae',
						borderWidth: 1,
					},
				],
			});
		}
	}, [genderPerRegionData]);

	const options = {
		responsive: true,
		// сделать столбцы уже
		datasets: {
			bar: {
				// абсолютная толщина столбца (px). Если много категорий, используйте maxBarThickness
				barThickness: 14,
				maxBarThickness: 18,
				// доля доступной категории, занимаемая столбцами
				categoryPercentage: 0.6,
				// доля группы, занимаемая каждым столбцом внутри категории
				barPercentage: 0.7,
			},
		},
		scales: {
			x: {
				title: {
					display: true,
					text: 'Регион',
					font: {
						size: 16,
					},
				},
				ticks: {
					font: {
						size: 13,
					},
				},
			},
			y: {
				beginAtZero: true,
				title: {
					display: true,
					text: 'Количество пациентов',
					font: {
						size: 16,
					},
				},
				ticks: {
					font: {
						size: 13,
					},
				},
			},
		},
		plugins: {
			legend: {
				position: 'top' as const,
			},
			tooltip: {
				callbacks: {
					label: (context: any) => `${context.dataset.label}: ${context.parsed.y} чел.`,
				},
			},
		},
	};

	const handleDownload = async () => {
		try {
			const result = await trigger().unwrap();
			const url = window.URL.createObjectURL(result);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', 'Отчёт по распределению пола по регионам.xlsx');
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Ошибка при скачивании файла:', error);
		}
	};

	if (isLoading) return <div>Загрузка...</div>;
	if (error) return <div>Ошибка загрузки данных</div>;

	return (
		<div className={styles.chart}>
			<h2>Распределение пациентов по полу и региону</h2>

			<div style={{ width: '100%', maxHeight: 500 }}>
				<Bar data={chartData} options={options} />
			</div>

			<Button onClick={handleDownload} loading={isFetching} size="xl">
				{isFetching ? 'Скачивание...' : 'Скачать отчёт по распределению пола по регионам'}
			</Button>
		</div>
	);
};
